import './perks-posts.component.scss';

import { h } from 'preact';
import { useState } from 'preact/hooks';

import { selectAccountSettings } from '../../../auth/store/auth.selectors';
import { SearchBar } from '../../../common/components/search-bar/search-bar.component';
import { SpinnerComponent } from '../../../common/components/spinner/spinner.component';
import { IconFilterBars } from '../../../common/icons/icon-filter-bars';
import { Pageable } from '../../../common/page/pageable.model';
import { useFeatureFlag } from '../../../feature-flags/hooks/use-feature-flag.hook';
import { FeatureFlagName } from '../../../feature-flags/models/feature-flag-names.enum';
import { useTranslation } from '../../../i18n';
import { selectMemberships } from '../../../membership/store/membership.selectors';
import { isContractFailed } from '../../../membership/utils/membership.utlis';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { buttonize } from '../../../utils/a11y.utils';
import { PerksHelper } from '../../perks.helper';
import {
  selectAllPosts,
  selectPostsLoading,
  selectPostsPage,
  selectPostsTotal,
  selectUser,
} from '../../store/perks.selectors';
import { loadPosts } from '../../store/perks.thunk';
import { PerksPostSubmission } from '../perks-post-submission/perks-post-submission.component';
import { PerksPostsSidebar } from '../perks-posts-sidebar/perks-posts-sidebar.component';
import { consumeSubmissionRequest } from './pending-submission';
import { PerksPostComponent } from './perks-post/perks-post.component';
import { scrollToPost } from './post-entry';
import { usePostFilterBar } from './post-filter-bar.context';

export const PerksPostsComponent = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectPostsLoading);
  const posts = useAppSelector(selectAllPosts);
  const postsPage = useAppSelector(selectPostsPage);
  const total = useAppSelector(selectPostsTotal);
  const user = useAppSelector(selectUser);
  const memberships = useAppSelector(selectMemberships);
  const accountSettingsResp = useAppSelector(selectAccountSettings);
  // the search + filter bar is owned by the perks wrapper but belongs in this column, so the
  // sidebar starts level with it instead of below a full-width bar
  const filterBar = usePostFilterBar();
  // artists and moderators publish directly, so the entry point says Create rather than Submit --
  // behind community-official-authors, like the attribution itself
  const officialAuthorsEnabled = useFeatureFlag(FeatureFlagName.COMMUNITY_OFFICIAL_AUTHORS);
  const skipsReview = officialAuthorsEnabled && (!!user?.officialAuthor || !!user?.moderator);
  // members only: the memberships list is the account's programs; a member is a user holding a
  // live contract on one of them (payment-failed contracts have already lost access)
  const isMember = !!memberships?.some((m) => !!m.contract && !isContractFailed(m.contract));
  // SNGL-7327: the artist is not a fan. Publishing to their own community is not a "community
  // submission", so neither gate on the fan path applies to them -- the membership contract and the
  // enableCommunitySubmissions setting both exist to control what fans can send in for review.
  // the submission endpoint 403s without community-submissions, so no entry point without it --
  // artists and moderators on a store that never enabled submissions post from the dashboard
  const submissionsEnabled = useFeatureFlag(FeatureFlagName.COMMUNITY_SUBMISSIONS);
  const canSubmitPosts =
    !!user &&
    submissionsEnabled &&
    (skipsReview ||
      (!!accountSettingsResp?.accountSettings?.enableCommunitySubmissions && isMember));
  // arriving from a post page's "Create a Post" -- the button there routes here to reach the form
  const [showSubmissionForm, setShowSubmissionForm] = useState(() => consumeSubmissionRequest());

  const handlePage = (pageNum: number) => {
    dispatch(
      loadPosts({
        pageable: Pageable.of(
          pageNum,
          PerksHelper.POSTS_DEFAULT_PAGE_SIZE,
          PerksHelper.POSTS_DEFAULT_SORT
        ),
        filter: filterBar.filter,
        loadMore: true,
      })
    );
  };

  // SNGL-7344: an author's or moderator's post that a gate covers goes live at once, so the
  // confirmation panel would only be a dead end. Return to the feed, reload its first page (pinned
  // posts first, then the new one) and scroll to the card once it renders. Off the first page --
  // filtered out, or behind a full page of pinned posts -- the top of the feed is as close as it gets.
  const handlePublished = async (postId: string) => {
    setShowSubmissionForm(false);
    const result = await dispatch(
      loadPosts({
        pageable: Pageable.of(
          0,
          PerksHelper.POSTS_DEFAULT_PAGE_SIZE,
          PerksHelper.POSTS_DEFAULT_SORT
        ),
        filter: filterBar.filter,
      })
    );
    if (loadPosts.fulfilled.match(result) && result.payload.content.some((p) => p.id === postId)) {
      scrollToPost(postId);
    }
  };

  return (
    <div class="single-posts-container">
      <div className="single-posts">
        {filterBar.visible && (
          <section class="single-filter">
            <SearchBar onSearch={filterBar.onSearch} onClear={filterBar.onSearch}>
              <div class="single-post-filter">
                <div
                  class={`single-post-filter-btn ${filterBar.hasActiveFilters ? 'active' : ''} `}
                  {...buttonize(() => filterBar.onOpenFilterModal?.(), t('perks.filterPosts'))}
                >
                  <IconFilterBars width={15} height={15} />
                </div>
              </div>
            </SearchBar>
          </section>
        )}

        {showSubmissionForm ? (
          <>
            <div class="go-back">
              <span {...buttonize(() => setShowSubmissionForm(false))}>
                {t('perks.backToFeed')}
              </span>
            </div>
            <PerksPostSubmission onPublished={handlePublished} />
          </>
        ) : (
          <>
            {canSubmitPosts && (
              <button
                class="button btn single-submit-post-mobile"
                onClick={() => setShowSubmissionForm(true)}
              >
                {t(skipsReview ? 'perks.submission.createPost' : 'perks.submission.submitPost')}
              </button>
            )}

            {loading && posts.length === 0 && (
              <section class="single-loading">
                <SpinnerComponent />
              </section>
            )}

            {posts && posts.length > 0 && (
              <>
                {posts.map((post) => (
                  <PerksPostComponent key={post.id} post={post} showTopComment={true} />
                ))}
              </>
            )}

            {!loading && posts && posts.length === 0 && (
              <div className="single-no-posts">
                <h2>{t('perks.noPosts')}</h2>
                <p>{t('perks.adjustFilters')}</p>
              </div>
            )}

            {total > 0 && (
              <section className="single-load-more">
                {postsPage && postsPage.totalPages > 1 && !postsPage.last && (
                  <button
                    className="button btn"
                    disabled={loading}
                    onClick={() => handlePage(postsPage.number + 1)}
                  >
                    {loading ? t('common.loading') : t('perks.loadMorePosts')}
                  </button>
                )}
              </section>
            )}
          </>
        )}
      </div>

      <PerksPostsSidebar onCreatePost={() => setShowSubmissionForm(true)} />
    </div>
  );
};
